// components/QuotationModal.jsx
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Modal, List, Tag, Button, message } from "antd";
import { externalBillService } from "../externalBillService";
import styles from "./QuotationModal.module.css";
import { HeartFilled, HeartTwoTone } from "@ant-design/icons";

interface RepairCompany {
    id: string;
    supplierName: string;
    itemAmount: number;
    itemNote: string;
    materialAmount: number;
    materialNote: string;
    [key: string]: any
}

interface Props {
    visible: boolean;
    onCancel: () => void;
    onConfirm: (selectedCompany: RepairCompany) => void;
    billId: string;
}

const QuotationModal: React.FC<Props> = ({
    visible,
    onCancel,
    onConfirm,
    billId,
}) => {
    const [loading, setLoading] = useState(false);
    const [repairCompanyList, setRepairCompanyList] = useState<RepairCompany[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<RepairCompany | null>(
        null
    );

    // 获取报修公司列表
    const fetchRepairCompanyList = useCallback(async () => {
        try {
            setSelectedCompany(null)
            setRepairCompanyList([])
            setLoading(true);
            const res = await externalBillService.one(billId);
            let retData = res.retData;
            if (retData.vrmExternalSends && retData.vrmExternalSends.length > 0) {
                setRepairCompanyList(retData.vrmExternalSends);
            } else {
                message.warning("词条单据无维修厂报价，至少有一家维修厂报价才可报价完成！");
            }
        } catch (error: any) {
            console.error("获取报修公司列表失败", error);
            message.error(error.message || "获取报修公司列表失败！");
        } finally {
            setLoading(false);
        }
    }, [billId]);

    // 选择报修公司
    const handleSelectCompany = (company: RepairCompany) => {
        setSelectedCompany(company);
    };

    // 处理报价完成确认
    const handleConfirm = async () => {
        if (!selectedCompany) {
            message.error("请选择报修公司");
            return;
        }

        try {
            setLoading(true);
            // 调用分配报修公司接口
            await externalBillService.externalRepairEnd({
                id: billId,
                checkedId: selectedCompany.id,
            });

            message.success("分配成功");
            onConfirm(selectedCompany);
            onCancel();
        } catch (error: any) {
            console.error("分配失败", error);
            message.error(error.message || "分配失败，请稍后再试");
        } finally {
            setLoading(false);
        }
    };

    // 格式化金额
    const formatAmount = (amount: number) => {
        return amount.toFixed(2);
    };

    // 渲染公司列表项
    const renderCompanyItem = (company: RepairCompany) => {
        const isSelected = selectedCompany?.id === company.id;
        return (
            <div
                key={company.id}
                className={`${styles.quoteCard} ${isSelected ? styles.selectedCompany : ""}`}
                onClick={() => handleSelectCompany(company)}
            >
                <div className={styles.quoteHeader}>
                    <span className={styles.quoteCompany}>单位名称：{company.supplierName}</span>
                </div>
                <div className={styles.quoteHeader}>
                    <span className={styles.quoteList}>是否拖车：{company.isTransport === 0 ? "是" : "否"}</span>
                </div>
                {company.isTransport === 0 && <div className={styles.quoteHeader}>
                    <span className={styles.quoteList}>路线：{company.route}</span>
                </div>}
                <div className={styles.quoteDividerSolid} />

                <div className={styles.quoteItem}>
                    <span className={styles.quoteLabel}>工时费用:</span>
                    <span className={styles.quoteValue}>{formatAmount(company.itemAmount)}</span>
                </div>

                <div className={styles.quoteItem}>
                    <span className={styles.quoteNote}>备注:</span>
                    <span className={styles.quoteNoteText}>{company.itemNote}</span>
                </div>

                <div className={styles.quoteDividerDashed} />

                <div className={styles.quoteItem}>
                    <span className={styles.quoteLabel}>配件费用:</span>
                    <span className={styles.quoteValue}>{formatAmount(company.materialAmount)}</span>
                </div>

                <div className={styles.quoteItem}>
                    <span className={styles.quoteNote}>备注:</span>
                    <span className={styles.quoteNoteText}>{company.materialNote}</span>
                </div>

                {isSelected && (
                    <div className={styles.selectedMark}>
                        <HeartFilled style={{ color: '#f92f60' }} />
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (visible) {
            fetchRepairCompanyList();
        }
    }, [visible]);

    return (
        <Modal
            title="选择报修公司"
            open={visible}
            onOk={handleConfirm}
            onCancel={onCancel}
            okText="完成报价"
            cancelText="取消"
            okButtonProps={{ loading }}
        >
            <div className={styles.companyList}>{repairCompanyList.map(renderCompanyItem)}</div>
            {selectedCompany && (
                <div className={styles.confirmCompany}>
                    <p className={styles.confirmText}>
                        是否确认将此条工单分配给 <span className={styles.companyName}>{selectedCompany.supplierName}</span>？
                    </p>
                </div>
            )}
        </Modal>
    );
};

export default QuotationModal;